import {
  IconTrashCan,
  IconPlus,
  GreyGrey50,
  GreyGrey70,
  Primary13Regular,
  SteppedNumberInput,
  PriceInput,
  SimpleTableColumn,
  SimpleTableInputProps,
  SimpleTableInvalidRow,
  SimpleTableRowActionsProps,
  formatWithCommas,
  Box,
  Flex
} from '@sequencehq/core-components'
import { FC, useCallback, useMemo } from 'react'
import {
  enforceMinimumPrecision,
  sanitizeTiers,
  ValidationError
} from '@sequencehq/utils'
import { PriceEditorTier } from 'common/drawers/PricingEditor/domain'
import { Currency } from '@sequencehq/api'

const tableInputStyle = {
  border: 'none',
  boxShadow: 'none',
  borderRadius: 'none'
}

const tableColumnsConfig: SimpleTableColumn<
  PriceEditorTier,
  keyof PriceEditorTier,
  {
    currency: Currency
  }
>[] = [
  {
    property: 'firstUnit',
    label: 'First unit',
    bodyComponent: ({ value, rowIndex }) => (
      <Box
        paddingLeft="12px"
        color={GreyGrey50}
        {...Primary13Regular}
        data-testid={`priceEditor.tiers.${rowIndex}.firstunit`}
      >
        {formatWithCommas(value ?? '')}
      </Box>
    )
  },
  {
    property: 'lastUnit',
    label: 'Last unit',
    bodyComponent: ({ value, onChange, onBlur, rowIndex, rows, disabled }) => {
      return (
        <SteppedNumberInput
          isDisabled={disabled || rowIndex === rows.length - 1}
          precision={0}
          onChange={newValue => {
            if (rowIndex === rows.length - 1) {
              return
            }
            return onChange(newValue)
          }}
          value={rowIndex === rows.length - 1 ? '∞' : (value ?? '')}
          {...tableInputStyle}
          paddingLeft="12px"
          onBlur={onBlur}
          placeholder="0"
          data-testid={`priceEditor.tiers.${rowIndex}.lastUnit`}
          onFocus={() => {
            if (value === '0') {
              onChange('')
            }
          }}
        />
      )
    }
  },
  {
    property: 'unitPrice',
    label: 'Unit price',
    bodyComponent: ({
      value,
      onBlur,
      onChange,
      disabled,
      config,
      rowIndex
    }) => (
      <PriceInput
        isDisabled={disabled}
        currency={config.currency}
        onChange={onChange}
        onBlur={onBlur}
        value={value ?? ''}
        variant="table"
        placeholder="0.00"
        onFocus={() => {
          if (value === '0.00') {
            onChange('')
          }
        }}
        data-testid={`priceEditor.tiers.${rowIndex}.unitPrice`}
        {...tableInputStyle}
      />
    )
  },
  {
    property: 'flatFee',
    label: 'Flat fee',
    bodyComponent: ({
      value,
      onBlur,
      onChange,
      disabled,
      config,
      rowIndex
    }) => (
      <PriceInput
        isDisabled={disabled}
        currency={config.currency}
        onChange={onChange}
        value={value ?? ''}
        color={parseFloat(value ?? '') === 0 ? GreyGrey50 : undefined}
        onBlur={onBlur}
        variant="table"
        placeholder="0.00"
        onFocus={() => {
          if (value === '0.00') {
            onChange('')
          }
        }}
        data-testid={`priceEditor.tiers.${rowIndex}.flatFee`}
        {...tableInputStyle}
      />
    )
  }
]

type UseFixedTierTableInput = (props: {
  onChange: (value: PriceEditorTier[]) => void
  value: PriceEditorTier[]
  isDisabled: boolean
  currency: Currency
  validationErrors: ValidationError[]
}) => {
  fieldConfig: SimpleTableInputProps<
    PriceEditorTier,
    {
      currency: Currency
    }
  > & {
    invalidRows: SimpleTableInvalidRow<PriceEditorTier>[]
    onBlur: () => void
  }
  addNewTier: (rowIndex?: number) => void
  test: {
    onDelete: (id: PriceEditorTier['id']) => void
    rowActionsAvailable: {
      delete: boolean
      addNewTier: boolean
    }
  }
}

export const useFixedTierTableInput: UseFixedTierTableInput = props => {
  const rowActionsAvailable = useMemo(() => {
    return {
      delete: props.value && props.value.length > 1 && !props.isDisabled,
      addNewTier: !props.isDisabled
    }
  }, [props])

  const onDelete = useCallback(
    (id: PriceEditorTier['id']) => {
      const newTiers = props.value.filter(tier => tier.id !== id)

      props.onChange(sanitizeTiers(newTiers))
    },
    [props]
  )

  const addNewTier = useCallback(
    (rowIndex: number = props.value.length - 1) => {
      const previousTier = props.value[rowIndex]

      const newTiers = sanitizeTiers([
        ...props.value.slice(0, rowIndex + 1),
        {
          id: crypto.randomUUID(),
          /** These will be calculated and set by the prepare tiers functionality */
          firstUnit: '',
          lastUnit: '',
          unitPrice: enforceMinimumPrecision(2)(previousTier?.unitPrice),
          flatFee: enforceMinimumPrecision(2)(previousTier?.flatFee)
        },
        ...props.value.slice(rowIndex + 1)
      ])
      props.onChange(newTiers)
    },
    [props]
  )

  const rowActionsComponent: FC<SimpleTableRowActionsProps<PriceEditorTier>> =
    useMemo(() => {
      return ({ rowData, rowIndex }) => {
        return (
          <Flex alignItems="center" height="100%" padding="0 8px" gap="4px">
            <Box
              as="span"
              display="inline-flex"
              data-test="fixed-delete-tier"
              color={rowActionsAvailable.delete ? GreyGrey70 : GreyGrey50}
              cursor={rowActionsAvailable.delete ? 'pointer' : undefined}
              _hover={{
                color: GreyGrey50
              }}
              onClick={() => rowActionsAvailable.delete && onDelete(rowData.id)}
            >
              <IconTrashCan color="inherit" />
            </Box>
            <Box
              as="span"
              display="inline-flex"
              data-test="fixed-add-new-tier"
              color={rowActionsAvailable.addNewTier ? GreyGrey70 : GreyGrey50}
              cursor="pointer"
              _hover={{
                color: GreyGrey50
              }}
              onClick={() => {
                rowActionsAvailable.addNewTier && addNewTier(rowIndex)
              }}
            >
              <IconPlus color="inherit" />
            </Box>
          </Flex>
        )
      }
    }, [onDelete, addNewTier, rowActionsAvailable])

  const rowValidationErrors: SimpleTableInvalidRow<PriceEditorTier>[] =
    useMemo(() => {
      return Object.values(
        props.validationErrors.reduce(
          (acc, error) => {
            const tierId = error.metadata?.tierId as string
            if (!tierId) {
              return acc
            }

            if (!acc[tierId]) {
              return {
                ...acc,
                [tierId]: {
                  id: tierId,
                  invalidProperties: [
                    error.metadata?.property as keyof PriceEditorTier
                  ]
                }
              }
            }

            return {
              ...acc,
              [tierId]: {
                ...acc[tierId],
                invalidProperties: [
                  ...(acc[tierId]?.invalidProperties ?? []),
                  error.metadata?.property as keyof PriceEditorTier
                ]
              }
            }
          },
          {} as Record<string, SimpleTableInvalidRow<PriceEditorTier>>
        )
      )
    }, [props.validationErrors])

  const onChange = useCallback(
    (newValue: PriceEditorTier[]) => {
      props.onChange(newValue)
    },
    [props]
  )

  const onBlur = useCallback(() => {
    props.onChange(sanitizeTiers(props.value))
  }, [props])

  return {
    addNewTier,
    fieldConfig: {
      columns: tableColumnsConfig,
      rowActions: {
        component: rowActionsComponent
      },
      invalidRows: rowValidationErrors,
      onChange,
      onBlur,
      rows: props.value,
      isDisabled: props.isDisabled,
      config: {
        currency: props.currency
      }
    },
    test: {
      onDelete,
      rowActionsAvailable
    }
  }
}
